import { useEffect } from 'react';
import { Lock, ArrowRight, FileText, Landmark, ShoppingCart, Package, Users, Calculator, BarChart3, Briefcase } from 'lucide-react';
import { Link } from 'react-router-dom';
import { trackJourneyStart, trackJourneyStep } from '../../lib/observability';

const MODULE_INFO: Record<string, { name: string; description: string; icon: React.ElementType; plan: string }> = {
  pos: {
    name: 'Point of Sale',
    description: 'Session-based checkout with hold/resume, multiple payment methods, and real-time inventory deduction.',
    icon: ShoppingCart,
    plan: 'Business',
  },
  invoices: {
    name: 'Invoicing & Contacts',
    description: 'Create Nepal-compliant tax invoices with VAT 13%, manage customers and suppliers, and track payments.',
    icon: FileText,
    plan: 'Starter',
  },
  khata: {
    name: 'Digital Khata',
    description: 'Double-entry bookkeeping with running balance, WhatsApp payment reminders, and downloadable statements.',
    icon: Landmark,
    plan: 'Starter',
  },
  expenses: {
    name: 'Expense Tracking',
    description: 'Categorized expense tracking with recurring templates and automatic cash book integration.',
    icon: Calculator,
    plan: 'Business',
  },
  reports: {
    name: 'Business Reports',
    description: 'Sales summaries, profit & loss, receivables aging, inventory valuation, VAT reports, and cash flow analysis.',
    icon: BarChart3,
    plan: 'Starter',
  },
  inventory: {
    name: 'Inventory Management',
    description: 'Multi-warehouse stock tracking, batch management, transfers, low-stock alerts, and demand forecasting.',
    icon: Package,
    plan: 'Business',
  },
  purchase: {
    name: 'Purchase Management',
    description: 'Manage suppliers, create purchase orders, track goods receipts, and handle purchase returns.',
    icon: Package,
    plan: 'Business',
  },
  hr: {
    name: 'HR & Payroll',
    description: 'Employee records, attendance tracking, leave management, and Nepal-specific PF/SSF/TDS payroll.',
    icon: Users,
    plan: 'Professional',
  },
  projects: {
    name: 'Projects & Timesheets',
    description: 'Project management with task tracking, timesheet logging, and billable hour tracking.',
    icon: Briefcase,
    plan: 'Professional',
  },
};

interface LockedModuleProps {
  moduleCode: string;
}

export const LockedModule: React.FC<LockedModuleProps> = ({ moduleCode }) => {
  const info = MODULE_INFO[moduleCode] || {
    name: moduleCode.charAt(0).toUpperCase() + moduleCode.slice(1),
    description: 'This feature is available on a higher subscription plan.',
    icon: Lock,
    plan: 'Higher Plan',
  };

  const Icon = info.icon;

  // BIZ-AUD-011: instrument the paywall-upgrade funnel so the business can measure how often a
  // hit paywall converts to viewing plans / contacting support (previously fired nothing).
  useEffect(() => {
    trackJourneyStart('paywall-upgrade', { module: moduleCode, required_plan: info.plan });
  }, [moduleCode, info.plan]);

  return (
    <div className="flex items-center justify-center min-h-[60vh] p-8">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100 dark:bg-slate-800">
          <Icon className="h-10 w-10 text-slate-400" />
        </div>

        <div className="inline-flex items-center gap-2 bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300 px-4 py-1.5 rounded-full text-xs font-bold mb-4">
          <Lock className="h-3 w-3" />
          {info.plan} Plan Required
        </div>

        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-3">
          {info.name}
        </h2>

        <p className="text-slate-500 dark:text-slate-400 mb-8 leading-relaxed">
          {info.description}
        </p>

        <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 mb-8">
          <p className="text-sm text-slate-600 dark:text-slate-300 mb-4">
            Ready to unlock {info.name}? Pick a plan below, or talk to our team to activate it on your account.
          </p>
          <Link
            to="/pricing"
            onClick={() => trackJourneyStep('paywall-upgrade', 'pricing-viewed', { module: moduleCode })}
            className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition-colors"
          >
            View Plans & Pricing
            <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">
            Need help choosing a plan or requesting activation? <Link to="/contact" onClick={() => trackJourneyStep('paywall-upgrade', 'support-contacted', { module: moduleCode })} className="font-medium text-indigo-600 hover:text-indigo-700">Talk to BizCore support</Link>.
          </p>
        </div>

        <Link
          to="/dashboard"
          className="text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 font-medium"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
};
